/**
 * Domain Costs — historical spend + forward-looking renewal forecast.
 *
 * Data source: GET /api/domain-costs/forecast returns bucketed
 * monthly totals (historical + projected) plus a per-domain
 * annualised summary. This page renders a calendar-style monthly
 * grid so operators can spot the heavy-renewal months at a glance
 * and feed them into the cashflow forecast.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, Globe2, TrendingUp, AlertTriangle, RefreshCw, CalendarDays } from "lucide-react";

const errMsg = (e, fb) => e?.response?.data?.detail || fb;
const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

function monthLabel(pm) {
  const [y, m] = pm.split("-").map(Number);
  const d = new Date(y, m - 1, 1);
  return d.toLocaleDateString("en-AU",
    { month: "short", year: "numeric" });
}
function currentPm() {
  return new Date().toISOString().slice(0, 7);
}

export default function DomainCostsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pastMonths, setPastMonths] = useState(12);
  const [futureMonths, setFutureMonths] = useState(24);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/domain-costs/forecast", {
        params: { past_months: pastMonths, future_months: futureMonths },
      });
      setData(d);
    } catch (e) {
      toast.error(errMsg(e, "Failed to load domain cost forecast"));
    } finally { setLoading(false); }
  }, [pastMonths, futureMonths]);

  useEffect(() => { load(); }, [load]);

  const nowPm = currentPm();
  const maxMonthTotal = useMemo(() =>
    Math.max(1, ...((data?.months || []).map((m) => m.total || 0))),
    [data]);

  return (
    <div className="p-6 space-y-5 max-w-[1400px]" data-testid="domain-costs-page">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-overline">Finance · Domain Costs</div>
          <h1 className="font-display font-extrabold text-3xl mt-1">Domain Cost Forecast</h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
            Historical spend + projected renewal costs per month across
            every domain in the registry, feeding directly into the
            cashflow forecast.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load}
            disabled={loading} data-testid="domain-costs-refresh">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </header>

      {/* KPIs */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="rounded-sm">
            <CardContent className="p-4">
              <div className="label-overline">Annualised (current)</div>
              <div className="font-display font-extrabold text-2xl mt-1"
                data-testid="domain-costs-annualised">
                {fmt$(data.totals.annualised_total)}
              </div>
              <div className="text-[11px] text-zinc-500 mt-1">
                {data.totals.domains_priced} domain(s) priced
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-sm">
            <CardContent className="p-4">
              <div className="label-overline">Projected · next {futureMonths}mo</div>
              <div className="font-display font-extrabold text-2xl mt-1"
                data-testid="domain-costs-projected">
                {fmt$(data.totals.projected_total)}
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-sm">
            <CardContent className="p-4">
              <div className="label-overline">Historical · last {pastMonths}mo</div>
              <div className="font-display font-extrabold text-2xl mt-1"
                data-testid="domain-costs-historical">
                {fmt$(data.totals.historical_total)}
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-sm border-amber-200 bg-amber-50/40">
            <CardContent className="p-4">
              <div className="label-overline text-amber-800">Missing data</div>
              <div className="font-display font-extrabold text-2xl mt-1 text-amber-900"
                data-testid="domain-costs-missing">
                {data.totals.domains_missing_price + data.totals.domains_missing_expiry}
              </div>
              <div className="text-[11px] text-amber-800 mt-1">
                {data.totals.domains_missing_price} unpriced · {data.totals.domains_missing_expiry} no expiry
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Window controls */}
      <Card className="rounded-sm">
        <CardContent className="p-4 flex items-center gap-4 text-xs flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-zinc-500">Past months:</span>
            <select value={pastMonths} onChange={(e) => setPastMonths(Number(e.target.value))}
              className="h-8 rounded-sm border border-zinc-200 px-2 text-xs bg-white"
              data-testid="domain-costs-past-months">
              {[6, 12, 24, 36].map((n) => <option key={n} value={n}>{n} months</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-zinc-500">Future months:</span>
            <select value={futureMonths} onChange={(e) => setFutureMonths(Number(e.target.value))}
              className="h-8 rounded-sm border border-zinc-200 px-2 text-xs bg-white"
              data-testid="domain-costs-future-months">
              {[12, 24, 36, 48].map((n) => <option key={n} value={n}>{n} months</option>)}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Calendar */}
      {loading ? (
        <Card className="rounded-sm"><CardContent className="p-8 flex items-center gap-2 text-zinc-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading domain cost forecast…
        </CardContent></Card>
      ) : (
        <Card className="rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <CalendarDays className="h-4 w-4 text-zinc-700" />
              <h3 className="font-display font-bold text-base">Monthly totals</h3>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2"
              data-testid="domain-costs-calendar">
              {(data?.months || []).map((m) => {
                const isCurrent = m.period_month === nowPm;
                const isPast = m.period_month < nowPm;
                const barPct = maxMonthTotal
                  ? Math.round((m.total / maxMonthTotal) * 100)
                  : 0;
                return (
                  <div key={m.period_month}
                    className={`border rounded-sm p-2 ${isCurrent
                      ? "border-[#002FA7] bg-[#002FA7]/5"
                      : isPast
                        ? "border-zinc-200 bg-zinc-50/60"
                        : "border-zinc-200 bg-white"}`}
                    data-testid={`domain-costs-month-${m.period_month}`}>
                    <div className="text-[10px] uppercase tracking-wide text-zinc-500">
                      {monthLabel(m.period_month)}
                    </div>
                    <div className="text-sm font-bold tabular-nums mt-0.5">
                      {fmt$(m.total)}
                    </div>
                    <div className="text-[10px] text-zinc-500 mt-0.5">
                      {m.domain_count} domain{m.domain_count === 1 ? "" : "s"}
                    </div>
                    <div className="mt-1 h-1.5 rounded-full bg-zinc-100 overflow-hidden">
                      <div className={`h-full ${isPast ? "bg-zinc-400" : "bg-[#002FA7]"}`}
                        style={{ width: `${barPct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
            {(data?.months || []).length === 0 && (
              <div className="text-center py-8 text-sm text-zinc-400 italic">
                No data — add a renewal cost to at least one domain and
                make sure the expiry date is set.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Per-domain summary */}
      {data && data.per_domain.length > 0 && (
        <Card className="rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Globe2 className="h-4 w-4 text-zinc-700" />
              <h3 className="font-display font-bold text-base">Per-domain annualised spend</h3>
              <Badge variant="outline" className="rounded-sm text-[10px]">
                {data.per_domain.length}
              </Badge>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]" data-testid="domain-costs-per-domain-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 border-b border-zinc-100">
                    <th className="text-left py-1.5 pr-2">Domain</th>
                    <th className="text-left py-1.5 px-2">Expiry</th>
                    <th className="text-left py-1.5 px-2">Charge</th>
                    <th className="text-right py-1.5 px-2">Annualised</th>
                    <th className="text-left py-1.5 pl-2">Auto-renew</th>
                  </tr>
                </thead>
                <tbody>
                  {data.per_domain.map((d) => (
                    <tr key={d.domain_id}
                      className="border-b border-zinc-100 last:border-b-0"
                      data-testid={`domain-costs-row-${d.domain_id}`}>
                      <td className="py-1.5 pr-2 font-mono">{d.domain}</td>
                      <td className="py-1.5 px-2 text-zinc-500">{d.expiry_date || "—"}</td>
                      <td className="py-1.5 px-2">
                        {fmt$(d.renewal_cost)}
                        <span className="text-[10px] text-zinc-400 ml-1">
                          / {d.frequency === "monthly" ? "mo"
                            : d.frequency === "quarterly" ? "qtr" : "yr"}
                        </span>
                      </td>
                      <td className="py-1.5 px-2 text-right font-mono font-semibold">
                        {fmt$(d.annualised_cost)}
                      </td>
                      <td className="py-1.5 pl-2">
                        {d.autorenew
                          ? <Badge variant="outline"
                              className="rounded-sm text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200">
                              on
                            </Badge>
                          : <Badge variant="outline"
                              className="rounded-sm text-[9px] bg-amber-50 text-amber-800 border-amber-200">
                              off
                            </Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Warnings */}
      {data && (data.totals.domains_missing_price > 0
                || data.totals.domains_missing_expiry > 0) && (
        <div className="text-[11px] text-amber-800 bg-amber-50/60 border border-amber-200 rounded-sm p-3 flex items-start gap-2"
          data-testid="domain-costs-warnings">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <div>
            Some domains aren&apos;t contributing to the forecast:
            <ul className="list-disc ml-5 mt-1 space-y-0.5">
              {data.totals.domains_missing_price > 0 && (
                <li>{data.totals.domains_missing_price} domain(s) have no
                  <b> renewal cost</b> — open the domain detail page and set one.</li>
              )}
              {data.totals.domains_missing_expiry > 0 && (
                <li>{data.totals.domains_missing_expiry} priced domain(s) have no
                  <b> expiry date</b> — refresh WHOIS from TPP.</li>
              )}
            </ul>
            <div className="text-[10px] text-amber-700 mt-1">
              Note: TPP&apos;s REST API doesn&apos;t expose historical
              invoice line items. Historical totals are derived from
              completed renewal orders on our platform priced at each
              domain&apos;s current <code>renewal_cost</code> — actual
              billed amounts may differ.
            </div>
          </div>
        </div>
      )}

      <div className="text-[10px] text-zinc-400 flex items-center gap-1">
        <TrendingUp className="h-3 w-3" />
        Data feeds into <code className="mx-1">/cashflow-forecast</code> as
        projected outflows on each domain&apos;s renewal date.
      </div>
    </div>
  );
}
